import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Minus, Plus, ShoppingBag, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { FALLBACK_PRODUCT_IMAGE } from '../data/products';
export function CartDrawer() {
  const {
    state,
    dispatch,
    cartTotal
  } = useCart();
  const navigate = useNavigate();
  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (state.isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [state.isOpen]);
  const handleClose = () => {
    dispatch({
      type: 'TOGGLE_CART',
      payload: false
    });
  };
  const handleCheckout = () => {
    handleClose();
    navigate('/checkout');
  };
  return <AnimatePresence data-id="element-39">
      {state.isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={handleClose} className="fixed inset-0 bg-black/40 z-50 backdrop-blur-sm" data-id="element-40" />

          {/* Drawer */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col" data-id="element-41">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100" data-id="element-42">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2" data-id="element-43">
                <ShoppingBag className="w-5 h-5" data-id="element-44" /> Your Cart
              </h2>
              <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors" data-id="element-45">
                <X className="w-5 h-5" data-id="element-46" />
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4" data-id="element-47">
              {state.items.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center space-y-4" data-id="element-48">
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-300" data-id="element-49">
                    <ShoppingBag className="w-8 h-8" data-id="element-50" />
                  </div>
                  <div data-id="element-51">
                    <p className="text-gray-900 font-medium" data-id="element-52">
                      Your cart is empty
                    </p>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-53">
                      Looks like you haven't added anything yet.
                    </p>
                  </div>
                  <button onClick={() => {
              handleClose();
              navigate('/shop');
            }} className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors" data-id="element-54">
                    Start Shopping
                  </button>
                </div> : <ul className="space-y-6" data-id="element-55">
                  {state.items.map(item => <li key={item.id} className="flex gap-4" data-id="element-56">
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-200" data-id="element-57">
                        <img src={item.product.images[0]} alt={item.product.name} onError={e => {
                  const el = e.currentTarget;
                  if (el.src !== FALLBACK_PRODUCT_IMAGE) el.src = FALLBACK_PRODUCT_IMAGE;
                }} className="w-full h-full object-cover" data-id="element-58" />
                      </div>
                      <div className="flex-1 flex flex-col" data-id="element-59">
                        <div className="flex justify-between items-start" data-id="element-60">
                          <div data-id="element-61">
                            <h3 className="text-sm font-medium text-gray-900 line-clamp-1" data-id="element-62">
                              {item.product.name}
                            </h3>
                            <div className="mt-1 text-xs text-gray-500 space-y-0.5" data-id="element-63">
                              {item.selectedSize && <p data-id="element-64">Size: {item.selectedSize}</p>}
                              {item.selectedColor && <p className="flex items-center gap-1" data-id="element-65">
                                  Color:
                                  <span className="w-2 h-2 rounded-full inline-block border border-gray-300" style={{
                          backgroundColor: item.selectedColor.hex
                        }} data-id="element-66" />
                                  {item.selectedColor.name}
                                </p>}
                              {item.selectedAddOns.map(addon => <p key={addon.id} data-id="element-67">+ {addon.name}</p>)}
                            </div>
                          </div>
                          <p className="text-sm font-semibold text-gray-900" data-id="element-68">
                            MVR {(item.unitPrice * item.quantity).toFixed(2)}
                          </p>
                        </div>

                        <div className="mt-auto flex items-center justify-between pt-2" data-id="element-69">
                          <div className="flex items-center border border-gray-200 rounded-md" data-id="element-70">
                            <button onClick={() => {
                      if (item.quantity > 1) {
                        dispatch({
                          type: 'UPDATE_QUANTITY',
                          payload: {
                            id: item.id,
                            quantity: item.quantity - 1
                          }
                        });
                      } else {
                        dispatch({
                          type: 'REMOVE_ITEM',
                          payload: item.id
                        });
                      }
                    }} className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-50" data-id="element-71">
                              <Minus className="w-3.5 h-3.5" data-id="element-72" />
                            </button>
                            <span className="w-8 text-center text-sm font-medium text-gray-900" data-id="element-73">
                              {item.quantity}
                            </span>
                            <button onClick={() => dispatch({
                      type: 'UPDATE_QUANTITY',
                      payload: {
                        id: item.id,
                        quantity: item.quantity + 1
                      }
                    })} className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-50" data-id="element-74">
                              <Plus className="w-3.5 h-3.5" data-id="element-75" />
                            </button>
                          </div>
                          <button onClick={() => dispatch({
                    type: 'REMOVE_ITEM',
                    payload: item.id
                  })} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1" data-id="element-76">
                            <Trash2 className="w-3.5 h-3.5" data-id="element-77" /> Remove
                          </button>
                        </div>
                      </div>
                    </li>)}
                </ul>}
            </div>

            {/* Footer */}
            {state.items.length > 0 && <div className="border-t border-gray-100 p-4 bg-gray-50" data-id="element-78">
                <div className="flex justify-between text-sm text-gray-600 mb-2" data-id="element-79">
                  <span data-id="element-80">Subtotal</span>
                  <span data-id="element-81">MVR {cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600 mb-4" data-id="element-82">
                  <span data-id="element-83">Shipping</span>
                  <span data-id="element-84">Calculated at checkout</span>
                </div>
                <div className="flex justify-between text-base font-semibold text-gray-900 mb-6" data-id="element-85">
                  <span data-id="element-86">Total</span>
                  <span data-id="element-87">MVR {cartTotal.toFixed(2)}</span>
                </div>
                <button onClick={handleCheckout} className="w-full bg-red-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-700 transition-colors flex justify-center items-center gap-2" data-id="element-88">
                  Proceed to Checkout
                </button>
                <button onClick={() => {
            handleClose();
            navigate('/cart');
          }} className="w-full mt-3 bg-white text-gray-700 border border-gray-300 py-3 px-4 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-89">
                  View Cart
                </button>
              </div>}
          </motion.div>
        </>}
    </AnimatePresence>;
}